// Descriptions are stored as markdown. Older rows hold a serialized Lexical
// editor state (JSON); those are converted on read via toMarkdown().

export type SerializedDescriptionNode = {
	type?: string;
	text?: string;
	format?: number | string;
	children?: SerializedDescriptionNode[];
	src?: string;
	alt?: string;
};

export function isLexicalState(value: string | null | undefined): boolean {
	if (!value) return false;
	try {
		const parsed = JSON.parse(value);
		return parsed?.root?.type === "root" && Array.isArray(parsed.root.children);
	} catch {
		return false;
	}
}

export function parseLexicalDescription(
	value: string | null | undefined,
): { root: SerializedDescriptionNode } | null {
	if (!value || !isLexicalState(value)) return null;
	try {
		return JSON.parse(value) as { root: SerializedDescriptionNode };
	} catch {
		return null;
	}
}

// Lexical text format bitmask -> markdown marks. Underline (8) has no
// markdown equivalent and is dropped.
const FORMAT_MARKS: [number, string][] = [
	[16, "`"],
	[4, "~~"],
	[2, "_"],
	[1, "**"],
];

function plainText(node: SerializedDescriptionNode): string {
	if (node.type === "linebreak") return "\n";
	if (typeof node.text === "string") return node.text;
	return (node.children ?? []).map(plainText).join("");
}

function inlineMarkdown(node: SerializedDescriptionNode): string {
	if (node.type === "linebreak") return "  \n";
	if (node.type === "image") return `![${node.alt ?? ""}](${node.src ?? ""})`;
	if (typeof node.text === "string") {
		const format = typeof node.format === "number" ? node.format : 0;
		// Marks must hug the text: "** bold **" is not bold in markdown.
		const [, lead, core, trail] = /^(\s*)([\s\S]*?)(\s*)$/.exec(node.text)!;
		if (!core) return node.text;
		const wrapped = FORMAT_MARKS.reduce(
			(text, [bit, mark]) => (format & bit ? `${mark}${text}${mark}` : text),
			core,
		);
		return `${lead}${wrapped}${trail}`;
	}
	return (node.children ?? []).map(inlineMarkdown).join("");
}

function blockMarkdown(node: SerializedDescriptionNode): string {
	if (node.type === "code") return `\`\`\`\n${plainText(node)}\n\`\`\``;
	return inlineMarkdown(node);
}

export function toMarkdown(value: string | null | undefined): string {
	const parsed = parseLexicalDescription(value);
	if (!parsed) return value ?? "";
	return (parsed.root.children ?? []).map(blockMarkdown).join("\n\n");
}

// ponytail: regex strip, not a markdown parser; good enough for summaries and emptiness checks
function stripMarkdown(md: string): string {
	return md
		.replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
		.replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+\.)\s+(\[[ xX]\]\s+)?/gm, "")
		.replace(/(\*\*|__|~~|`+)/g, "");
}

export function projectDescriptionText(value: string | null | undefined): string {
	if (!value) return "";
	return stripMarkdown(toMarkdown(value)).replace(/\s+/g, " ").trim();
}
